import { AvailabilityState } from "@hooplab/ui-components/dist/types";

declare global {
  type Environment = "production" | "production-uk" | "staging" | "staging-uk" | "local";
  type EnvironmentVariables = {
    environment: string;
    __IMGIX_ENV__: "production" | "production-uk" | "staging" | "staging-uk";
    __HOOPLA_GTM__: string;
    __HOOPLA_GA__: string;
    __HOOPLA_GA4__: string;
    __SENTRY_DSN__: string;
    __MAPTILER_API_KEY__: string;
    __NETS_CHECKOUT_KEY__: string;
    __NETS_CHECKOUT_URL__: string;
  };

  interface Window {
    ORGANIZATION_ID: string;
    ORGANIZATION_GOOGLE_ANALYTICS: string;
    ORGANIZATION_FB_PIXEL: string;
    ORGANIZATION_ENHANCED_MEASUREMENT: "true" | "false"; // this is dumb
    ORGANIZATION_CURRENCY: string;
    ORGANIZATION_LOCALE: string;
    AVAILABLE_LOCALES: string[];
    ORGANIZATION_REGION: string;
    ORGANIZATION_TIMEZONE: string;
    Dibs: any;

    ENVIRONMENT: EnvironmentVariables;
    env: Environment;
    HooplaSalesPage: {
      mount: (el: any, props: any) => void;
      unmount: (el: any) => void;
    };
  }
}

export type Locale = "en" | "no";

export type LocaleData = {
  name: string;
  alternate: string;
  short: string;
  flag: React.ReactNode;
  text: React.ReactNode;
  messages: any;
};

export type LocaleConfig = {
  [k in Locale]: LocaleData;
};

export type PriceMode =
  | "PRICE_MODE_DEFAULT"
  | "PRICE_MODE_EXCLUDE_VAT"
  | "PRICE_MODE_EXCLUDE_FEE"
  | "PRICE_MODE_EXCLUDE_FEE_AND_VAT"
  | "PRIVATE/SHOW_FEE"
  | "PRIVATE/SPECIFIED_FEE"
  | "PRIVATE/NO_FEE"
  | "BUSINESS/SHOW_FEE"
  | "BUSINESS/SPECIFIED_FEE"
  | "BUSINESS/NO_FEE";

type Pending = {
  status: "pending";
};

type PendingWithPreviousResult<T> = {
  status: "pending_with_previous_result";
  entity: T;
};

export type Fetched<T> = {
  status: "fetched";
  entity: T;
};

export type RequestError<E> = {
  status: "error";
  error: E;
};

export type BackendRequest<T, E> =
  | Pending
  | PendingWithPreviousResult<T>
  | Fetched<T>
  | RequestError<E>;

export type Organization = {
  name: string;
  description: string;
  priceMode: PriceMode;
  location: string | null;
  website: string | null;
  images: OrganizationImageCrops | null;
  image: ImgixImage | null;
  mdDescription: boolean;
};

export type ImageCrop = {
  url: string;
  imageId: number;
  height: number;
  width: number;
  x: number;
  y: number;
};

export type EventImageCrops = {
  eventId: number;
  crop16x9: ImageCrop;
  crop4x3: ImageCrop;
  crop1x1: ImageCrop;
};

export type OrganizationImageCrops = {
  organizationId: number;
  crop16x9: ImageCrop;
  crop4x3: ImageCrop;
  crop1x1: ImageCrop;
};

export type CountRule = {
  min: number | null;
  max: number | null;
  mod: number | null;
};

export type EventListItem = {
  eventId: number;
  identifier: string | null;
  title: string;
  subtitle: string;
  location: string;
  start: Date;
  end: Date;
  image: ImgixImage | null;
  images: EventImageCrops | null;
  dateFormat: DateFormat;
  category: string;
  saleState: string;
  availability: AvailabilityState;
};

export type TicketState = "SOLD_OUT" | "FEATURED" | "PROMO" | "NONE" | "POPULAR";

export type TicketTypeCartItem = {
  ticketTypeId: number;
  name: string;
  price: number;
  fee: number;
  vatFactor: number;
  description?: string;
  secretKey: string | null;
  hasNonRequiredAddons: boolean;
  requiredAddonProducts: AddonProductCartItem[];
  availabilityStatus: AvailabilityState;
  countRule: CountRule;
  validFrom: Date | null;
  validTo: Date | null;
  isFullDay: boolean;
  isSecret: boolean;
  isFeatured: boolean | null;
};

export type TicketTypeGroup = {
  ticketType: TicketTypeCartItem;
  ticketTypeGroupId: number;
  ordering: number;
  promoted: boolean;
};

export type TicketTypeGroupSetting = {
  ticketTypeGroupId: number;
  eventId: number;
  name: string;
  description: string | null;
  validFrom: Date | null;
  validTo: Date | null;
  isFullDay: boolean;
  ordering: number;
  availabilityStatus: AvailabilityState | null;
};

export type GroupedTicketTypes = {
  ticketTypeGroup: TicketTypeGroupSetting | null;
  ticketTypes: TicketTypeGroup[];
};

export type AddonProductCartItem = {
  addonProductId: number;
  name: string;
  price: number;
  fee: number;
  vatFactor: number;
  description?: string;
  customerPrompt?: string;
  required?: number;
  hidePrice: boolean;
  soldOut: boolean;
  minPerTicket?: number | null;
  maxPerTicket?: number | null;
  linkedTicketTypes: number[];
};

export type ImgixCrop = {
  height: number;
  width: number;
  offsetX: number;
  offsetY: number;
};

export type ImgixImage = {
  url: string;
  crop: ImgixCrop;
};

export type InformationBlock = {
  header: string;
  info: string;
  informationType: string;
};

type AddressInfo = {
  streetAddress: string;
  postalArea: string;
  postalCode: string;
};

export type EventInfo = {
  eventId: string;
  name: string;
  subtitle: string;
  ingress: string;
  isCancelled: boolean;
  description: string;
  isLivestream: boolean;
  information: InformationBlock[];
  dateFormat: DateFormat | null;
  invoiceAllowed: boolean;

  images: EventImageCrops | null;
  image: ImgixImage | null;

  start: Date;
  end: Date;

  location: string;
  mapData: MapData;
  saleState: string;
  address: AddressInfo | null;
  directions: string;
  hasPromoCodes: boolean;
  maxPerTicket: number;
  availability: AvailabilityState;
  category: string;
};

export type DateFormat = {
  dateType: string;
  dateText: string;
};

export type MapData = {
  latitude: string | null;
  longitude: string | null;
  mapImage: boolean;
};

export type ReservedTicketType = {
  ticketTypeName: string;
  ticketTypeId: number;
  addonProducts: ReservedAddonProduct[];
  price: number;
  fee: number;
  vatFactor: number;
  eventId: number;
};

export type ReservedAddonProduct = {
  addonProductId: number;
  comment: string;
};

type NormalField = {
  required: boolean;
  type: "input" | "textarea" | "checkbox" | "integer-input" | "email-input" | "personalia";
  uuid: string;
  title: string;
};

type ValuesField = {
  required: boolean;
  type: "radiogroup" | "select" | "checkboxgroup";
  uuid: string;
  title: string;
  values: string[];
};

type SeperatorField = {
  type: "divider";
  title: string;
  text: string;
  uuid: string;
};
export type Field = NormalField | ValuesField | SeperatorField;

export type FieldTypes =
  | "input"
  | "textarea"
  | "checkbox"
  | "integer-input"
  | "email-input"
  | "personalia"
  | "radiogroup"
  | "select"
  | "checkboxgroup"
  | "divider";

export type Form = {
  formId: number;
  name: string;
  fields: Field[];
  json_schema: any;
};

export type NameAnswer = { first_name: string; last_name: string };

export type FieldAnswerValue = string | string[] | number | boolean | NameAnswer | null;

export type FormWithIds = {
  answerId: string;
  form: Form;
  eventForm: (ReservationEventForm | ReservationTicketTypeForm)[] | null;
};

export type FormWithAnswers = {
  answerId: string;
  formId: number;
  formGroupId: string;
  answers: AllFormAnswers;
};

export type PurchaseForm = { eventId: number; formGroupId: string; forms: FormWithIds[] };
export type TicketTypeForm = {
  ticketTypeId: number;
  formGroupId: string;
  ticketTypeName: string;
  forms: FormWithIds[];
};

export type ReservationEventForm = {
  eventId: number;
  formWithAnswers: FormWithAnswers[];
};

export type AcceptedCheckoutMethodV2 =
  | "v2/nets"
  | "v2/free"
  | "v2/nets_vipps"
  | "v2/nets_invoice"
  | "v2/two/bought";

export type CheckoutMethod = {
  checkout_method: AcceptedCheckoutMethodV2;
  fee_type: string;
  fee: number;
};

export type SmsSettings = {
  org_covers_sms_fee: boolean;
  sms_fee: number;
};

export type InsuranceSettings = {
  insurance_available: boolean;
  insurance_fee: number;
};

export type CheckoutMethodsData = {
  methods: CheckoutMethod[];
  consents: ConsentsData;
  summary: {
    total_price: number;
    invoice_lines: InvoiceLineSpec[];
    addons: number;
    tickets: number;
  };
  reservation_last_touched: Date;
  sms_settings: SmsSettings;
  insurance_settings: InsuranceSettings;
};

export type SummaryAdditionalFee = {
  description: React.ReactNode;
  amount: number;
  vat: number;
};

export type FreeCheckoutData = {
  first_name: string;
  last_name: string;
  email: string;
  phone_country_code: string;
  phone_number: string;
  hoopla_marketing_consent: boolean;
  send_sms: boolean;
  organization_marketing_consent: boolean;
};

export type Consents = {
  send_sms: boolean;
  include_insurance: boolean | undefined;
  hoopla_marketing_consent: boolean;
  organization_marketing_consent: boolean;
};

export type ConsentsData = {
  organizer_consent_text: string | null;
  organizer_consent_active: boolean;
} & Consents;

export type FreeCheckoutDetails = {
  ticket_type_id: number | null;
  addon_product_id: number | null;
  description: string;
};

export type ReservationTicketTypeForm = {
  ticketTypeId: number;
  formWithAnswers: FormWithAnswers[];
};

export type ExistingFormData = {
  eventId: number;
  events: ReservationEventForm[];
  ticketTypes: ReservationTicketTypeForm[];
};

export type ReservationForms = {
  purchaseForms: PurchaseForm[];
  ticketTypeForms: TicketTypeForm[];
};

export type Reservation = {
  reservationId: number;
  lastTouched: Date;
  checkoutLock: boolean;
  tickets: ReservedTicketType[];
  forms: ReservationForms;
  existingFormData: ExistingFormData;
  promoCodes: string[];
};

export const ticketTypeHasForms = (ttf: TicketTypeForm) => ttf.forms.length > 0;

export type Answer = { value: FieldAnswerValue; required: boolean; type: FieldTypes };
export type FormAnswer = { [uuid: string]: Answer };

export type AllFormAnswers = {
  [answerId: string]: FormAnswer;
};

export type FormFieldError = string; // eller noe

export type AnswerIdErrors = { [answerId: string]: { [uuid: string]: FormFieldError[] } };
export type AllFormErrors = {
  [formGroupId: string]: AnswerIdErrors;
};

export const hasErrors = (errors: AllFormErrors): boolean =>
  Object.values(errors)
    .filter((x) => Object.keys(x).length > 0)
    .map((x) => Object.values(x))
    .reduce((acc: boolean, answers) => {
      const hasErrs = answers.reduce((acc, x) => acc || Object.values(x).length > 0, false);
      return acc || hasErrs;
    }, false);

type BaseInvoiceLine = {
  amount: number;
  vat_factor: number;
  vat: number;
  description: string;
};

export type TicketFeeInvoiceLine = BaseInvoiceLine & {
  invoice_line_type: "ticket_fee";
  ticket_type_id: string;
  ticket_id: string;
};
export type TicketInvoiceLine = BaseInvoiceLine & {
  invoice_line_type: "ticket";
  ticket_type_id: string;
  ticket_id: string;
};

export type AddonProductFeeInvoiceLine = BaseInvoiceLine & {
  invoice_line_type: "addon_product_fee";
  addon_product_id: string;
  purchased_addon_product_id: string;
  ticket_id: string;
};
export type AddonProductInvoiceLine = BaseInvoiceLine & {
  invoice_line_type: "addon_product";
  addon_product_id: string;
  purchased_addon_product_id: string;
  ticket_id: string;
};

export type HooplaFeeInvoiceLine = BaseInvoiceLine & {
  invoice_line_type: "hoopla_fee";
  fee_type: string;
};

export type InsuranceFeeInvoiceLine = BaseInvoiceLine & {
  invoice_line_type: "insurance_fee";
};

export type TextInvoiceLine = BaseInvoiceLine & {
  invoice_line_type: "text";
};

export const isTicketInvoiceLine = (il: InvoiceLine | InvoiceLineSpec): il is TicketInvoiceLine =>
  il.invoice_line_type === "ticket";

export const isAddonProductInvoiceLine = (
  il: InvoiceLine | InvoiceLineSpec,
): il is AddonProductInvoiceLine => il.invoice_line_type === "addon_product";

export const isTicketFeeInvoiceLine = (
  il: InvoiceLine | InvoiceLineSpec,
): il is TicketFeeInvoiceLine => il.invoice_line_type === "ticket_fee";

export const isHooplaFeeInvoiceLine = (
  il: InvoiceLine | InvoiceLineSpec,
): il is HooplaFeeInvoiceLine => il.invoice_line_type === "hoopla_fee";

export const isInsuranceFeeInvoiceLine = (
  il: InvoiceLine | InvoiceLineSpec,
): il is InsuranceFeeInvoiceLine => il.invoice_line_type === "insurance_fee";

export const isAddonFeeInvoiceLine = (
  il: InvoiceLine | InvoiceLineSpec,
): il is AddonProductFeeInvoiceLine => il.invoice_line_type === "addon_product_fee";

export type InvoiceLine =
  | TicketFeeInvoiceLine
  | TicketInvoiceLine
  | HooplaFeeInvoiceLine
  | InsuranceFeeInvoiceLine
  | AddonProductFeeInvoiceLine
  | AddonProductInvoiceLine
  | TextInvoiceLine;

type BaseInvoiceLineSpec = {
  amount: number;
  vat_factor: number;
  vat: number;
  description: string;
};

export type TicketFeeInvoiceLineSpec = BaseInvoiceLineSpec & {
  invoice_line_type: "ticket_fee";
  ticket_type_id: string;
};
export type TicketInvoiceLineSpec = BaseInvoiceLineSpec & {
  invoice_line_type: "ticket";
  ticket_type_id: string;
};

export type AddonProductFeeInvoiceLineSpec = BaseInvoiceLineSpec & {
  invoice_line_type: "addon_product_fee";
  addon_product_id: string;
};
export type AddonProductInvoiceLineSpec = BaseInvoiceLineSpec & {
  invoice_line_type: "addon_product";
  addon_product_id: string;
};

export const isTicketInvoiceLineSpec = (il: InvoiceLineSpec): il is TicketInvoiceLineSpec =>
  il.invoice_line_type === "ticket";

export const isAddonProductInvoiceLineSpec = (
  il: InvoiceLineSpec,
): il is AddonProductInvoiceLineSpec => il.invoice_line_type === "addon_product";

export const isTicketFeeInvoiceLineSpec = (il: InvoiceLineSpec): il is TicketFeeInvoiceLineSpec =>
  il.invoice_line_type === "ticket_fee";

export const isAddonFeeInvoiceLineSpec = (
  il: InvoiceLineSpec,
): il is AddonProductFeeInvoiceLineSpec => il.invoice_line_type === "addon_product_fee";

export type InvoiceLineSpec =
  | TicketFeeInvoiceLineSpec
  | TicketInvoiceLineSpec
  | AddonProductFeeInvoiceLineSpec
  | AddonProductInvoiceLineSpec;

export type BrregOrganization = {
  name: string;
  idNumber: string;
  address: string;
  zipCode: string;
  city: string;
};

export type TicketWithTicketTypeId = {
  ticketId: string;
  ticketTypeId: number;
};

export type TicketTypeDate =
  | {
      validFrom: Date;
      validTo: Date;
      isFullDay: boolean;
    }
  | "see_ticket";

export type Order = {
  orderId: string;
  processor: string;
  invoiceLines: InvoiceLine[];
  firstName: string;
  klarnaHtml: string;
  purchaseId: string;
  hash: string;
  refCode: string;
  ticketTypeDate: TicketTypeDate | null;
  event: {
    eventId: string;
    name: string;
    start: Date;
    end: Date;
    dateFormat: DateFormat;
    ticketDate: string | null;
    address: string | null;
    location: string | null;
  };
  tickets: TicketWithTicketTypeId[];
};

export type ConsentRequest = {
  id: number;
  text: string;
};
